import React, {
    useState,
    useCallback,
    useMemo,
    useEffect,
    useRef,
} from 'react';
import {
    Drawer,
    Text,
    Badge,
    Button,
    Flex,
    Stack,
    ActionIcon,
    Group,
    Anchor,
    Progress,
} from '@mantine/core';
import {
    IconPhone,
    IconMail,
    IconX,
    IconStarFilled,
    IconMapPin,
} from '@tabler/icons-react';
import { When, UnsavedChangesWarningModal } from '../common';
import { SurveyProofUpload, ProofFile } from './SurveyProofUpload';
import {
    showSuccessNotification,
    showErrorNotification,
} from '../../notifications';
import stringifyNumber from '../../utility/stringifyNumber';
import { useGetProofOfSurveyCompetitorPricing } from '../../services/survey/survey.query';
import {
    useGenerateSurveyProofUploadUrls,
    useCreateProofOfSurveyCompetitorPricing,
    useDeleteSurveyCompetitorProof,
} from '../../services/survey/survey.mutation';
import type { ProofDocumentForModal } from '../requests/components/ProofOfPricingModal';

export type OpenProofViewerPayload = {
    documents: ProofDocumentForModal[];
    initialIndex: number;
};

export interface CompetitorData {
    priceSurveyCompetitorId: string;
    competitor: {
        id: string;
        name: string;
        email?: string;
        address?: string;
        isMajor?: boolean;
        telNo?: {
            countryCode?: string;
            dialingCode?: string;
            number?: string;
        };
    };
}

interface SurveyCompetitorDrawerProps {
    opened: boolean;
    onClose: () => void;
    competitorData: CompetitorData | null;
    onProofsUpdated?: () => void;
    readOnly?: boolean;
    onOpenProofViewer?: (payload: OpenProofViewerPayload) => void;
    ensurePricesSavedForProofUpload?: (
        competitorId: string,
        currentPriceSurveyCompetitorId: string,
    ) => Promise<string | null>;
    isSavingPrices?: boolean;
}

type CompetitorProofDraft = {
    pendingProofs: ProofFile[];
    proofsToDelete: string[];
};

const emptyDraft = (): CompetitorProofDraft => ({
    pendingProofs: [],
    proofsToDelete: [],
});

function s3KeyFromPresignedUrl(uploadUrl: string): string {
    const encodedPath = new URL(uploadUrl).pathname.slice(1);
    try {
        return decodeURIComponent(encodedPath);
    } catch {
        return encodedPath;
    }
}

export function SurveyCompetitorDrawer({
    opened,
    onClose,
    competitorData,
    onProofsUpdated,
    readOnly = false,
    onOpenProofViewer,
    ensurePricesSavedForProofUpload,
    isSavingPrices = false,
}: SurveyCompetitorDrawerProps) {
    const [draftsByCompetitorId, setDraftsByCompetitorId] = useState<
        Record<string, CompetitorProofDraft>
    >({});
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] =
        useState(false);
    const updateInFlightRef = useRef(false);

    const priceSurveyCompetitorId =
        competitorData?.priceSurveyCompetitorId || '';

    const currentDraft = priceSurveyCompetitorId
        ? draftsByCompetitorId[priceSurveyCompetitorId]
        : undefined;
    const pendingProofs = currentDraft?.pendingProofs ?? [];
    const proofsToDelete = currentDraft?.proofsToDelete ?? [];

    useEffect(() => {
        if (!opened) {
            setUnsavedChangesModalOpen(false);
        }
    }, [opened]);

    const shouldSkipProofsQuery = !opened || !priceSurveyCompetitorId;
    const { data: proofsData, refetch: refetchProofs } =
        useGetProofOfSurveyCompetitorPricing(
            priceSurveyCompetitorId,
            shouldSkipProofsQuery,
        );

    const [generateUploadUrls] = useGenerateSurveyProofUploadUrls();
    const [createProofs] = useCreateProofOfSurveyCompetitorPricing();
    const [deleteProof] = useDeleteSurveyCompetitorProof();

    const existingProofs: ProofFile[] = useMemo(() => {
        if (!proofsData?.getProofOfSurveyCompetitorPricing) return [];
        return proofsData.getProofOfSurveyCompetitorPricing.map(proof => ({
            id: proof.id,
            fileName: proof.fileName,
            fileSize: proof.fileSize ?? undefined,
            fileType: proof.fileType ?? undefined,
            downloadUrl: proof.downloadUrl ?? undefined,
            documentS3Path: proof.documentS3Path,
            isNew: false,
        }));
    }, [proofsData]);

    const allProofs = useMemo(() => {
        const existingWithFlags: ProofFile[] = existingProofs.map(p => ({
            ...p,
            pendingRemove: Boolean(p.id && proofsToDelete.includes(p.id)),
        }));
        return [...existingWithFlags, ...pendingProofs];
    }, [existingProofs, pendingProofs, proofsToDelete]);

    const objectUrls = useMemo(
        () =>
            allProofs.map(p =>
                !p.downloadUrl && p.file ? URL.createObjectURL(p.file) : null,
            ),
        [allProofs],
    );

    useEffect(() => {
        return () => {
            objectUrls.forEach(url => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, [objectUrls]);

    const modalDocuments: ProofDocumentForModal[] = useMemo(() => {
        return allProofs.map((p, index) => ({
            details: {
                fileName: p.fileName,
                fileType: p.fileType,
            },
            downloadUrl: p.downloadUrl || objectUrls[index],
            documentS3Path: p.documentS3Path,
        }));
    }, [allProofs, objectUrls]);

    const handleAddProofs = useCallback(
        (files: File[]) => {
            if (!priceSurveyCompetitorId) return;
            const newProofs: ProofFile[] = files.map(file => ({
                fileName: file.name.trim(),
                fileSize: file.size,
                fileType: file.type,
                file,
                isNew: true,
            }));
            setDraftsByCompetitorId(prev => {
                const cur = prev[priceSurveyCompetitorId] ?? emptyDraft();
                return {
                    ...prev,
                    [priceSurveyCompetitorId]: {
                        ...cur,
                        pendingProofs: [...cur.pendingProofs, ...newProofs],
                    },
                };
            });
        },
        [priceSurveyCompetitorId],
    );

    const handleRemoveProof = useCallback(
        (proof: ProofFile) => {
            if (!priceSurveyCompetitorId) return;
            setDraftsByCompetitorId(prev => {
                const cur = prev[priceSurveyCompetitorId] ?? emptyDraft();
                if (proof.isNew) {
                    return {
                        ...prev,
                        [priceSurveyCompetitorId]: {
                            ...cur,
                            pendingProofs: cur.pendingProofs.filter(
                                p =>
                                    p.fileName !== proof.fileName ||
                                    p.file !== proof.file,
                            ),
                        },
                    };
                }
                if (proof.id) {
                    if (cur.proofsToDelete.includes(proof.id)) {
                        return {
                            ...prev,
                            [priceSurveyCompetitorId]: {
                                ...cur,
                                proofsToDelete: cur.proofsToDelete.filter(
                                    id => id !== proof.id,
                                ),
                            },
                        };
                    }
                    return {
                        ...prev,
                        [priceSurveyCompetitorId]: {
                            ...cur,
                            proofsToDelete: [...cur.proofsToDelete, proof.id],
                        },
                    };
                }
                return prev;
            });
        },
        [priceSurveyCompetitorId],
    );

    const handleViewProof = useCallback(
        (proof: ProofFile) => {
            const index = allProofs.findIndex(
                p =>
                    p === proof ||
                    (p.id && p.id === proof.id) ||
                    (p.fileName === proof.fileName && p.file === proof.file),
            );
            if (index !== -1 && onOpenProofViewer) {
                onOpenProofViewer({
                    documents: modalDocuments,
                    initialIndex: index,
                });
            }
        },
        [allProofs, modalDocuments, onOpenProofViewer],
    );

    const hasChanges = pendingProofs.length > 0 || proofsToDelete.length > 0;

    const discardDraftAndClose = useCallback(() => {
        if (priceSurveyCompetitorId) {
            setDraftsByCompetitorId(prev => {
                const next = { ...prev };
                delete next[priceSurveyCompetitorId];
                return next;
            });
        }
        setUnsavedChangesModalOpen(false);
        onClose();
    }, [priceSurveyCompetitorId, onClose]);

    const handleCloseAttempt = useCallback(() => {
        if (isUploading || isSavingPrices || updateInFlightRef.current) {
            return;
        }
        if (hasChanges) {
            setUnsavedChangesModalOpen(true);
        } else {
            onClose();
        }
    }, [isUploading, isSavingPrices, hasChanges, onClose]);

    const handleCancel = handleCloseAttempt;

    const handleUpdate = useCallback(async () => {
        if (!priceSurveyCompetitorId || updateInFlightRef.current) {
            return;
        }

        const competitorId = competitorData?.competitor?.id;
        let resolvedPriceSurveyCompetitorId = priceSurveyCompetitorId;

        updateInFlightRef.current = true;

        try {
            if (ensurePricesSavedForProofUpload && competitorId) {
                const savedId = await ensurePricesSavedForProofUpload(
                    competitorId,
                    priceSurveyCompetitorId,
                );
                if (!savedId) {
                    return;
                }
                resolvedPriceSurveyCompetitorId = savedId;
                if (savedId !== priceSurveyCompetitorId) {
                    setDraftsByCompetitorId(prev => {
                        const draft = prev[priceSurveyCompetitorId];
                        if (!draft) {
                            return prev;
                        }
                        const next = { ...prev };
                        delete next[priceSurveyCompetitorId];
                        next[savedId] = draft;
                        return next;
                    });
                }
            }

            setIsUploading(true);
            setUploadProgress(5);

            if (proofsToDelete.length > 0) {
                await Promise.all(
                    proofsToDelete.map(proofId =>
                        deleteProof({ variables: { proofId } }),
                    ),
                );
                setUploadProgress(pendingProofs.length > 0 ? 22 : 92);
            }

            if (pendingProofs.length > 0) {
                const fileNames = pendingProofs.map(p => p.fileName);
                const fileCount = pendingProofs.filter(p => p.file).length;

                const { data: urlsData } = await generateUploadUrls({
                    variables: {
                        input: {
                            priceSurveyCompetitorId:
                                resolvedPriceSurveyCompetitorId,
                            fileNames,
                        },
                    },
                });

                const { urls } = urlsData?.generateSurveyProofUploadUrls ?? {};
                if (!urls?.length || urls.length !== pendingProofs.length) {
                    throw new Error('Failed to get upload URLs');
                }

                setUploadProgress(32);

                let uploaded = 0;
                await pendingProofs.reduce<Promise<void>>(
                    async (previous, proof, index) => {
                        await previous;
                        if (!proof.file) {
                            return;
                        }

                        const response = await fetch(urls[index], {
                            method: 'PUT',
                            body: proof.file,
                            headers: {
                                'Content-Type':
                                    proof.fileType ||
                                    'application/octet-stream',
                            },
                        });

                        if (!response.ok) {
                            throw new Error(
                                `Failed to upload ${proof.fileName}`,
                            );
                        }

                        uploaded += 1;
                        setUploadProgress(
                            32 +
                                Math.round(
                                    (52 * uploaded) / Math.max(fileCount, 1),
                                ),
                        );
                    },
                    Promise.resolve(),
                );

                const uploads = pendingProofs.map((proof, index) => {
                    return {
                        documentS3Path: s3KeyFromPresignedUrl(urls[index]),
                        fileName: proof.fileName,
                        fileSize: proof.fileSize,
                        fileType: proof.fileType,
                    };
                });

                setUploadProgress(90);
                await createProofs({
                    variables: {
                        input: {
                            priceSurveyCompetitorId:
                                resolvedPriceSurveyCompetitorId,
                            uploads,
                        },
                    },
                });
                setUploadProgress(100);
            } else if (proofsToDelete.length > 0) {
                setUploadProgress(100);
            }

            showSuccessNotification('Proofs updated successfully');
            setDraftsByCompetitorId(prev => {
                const next = { ...prev };
                delete next[priceSurveyCompetitorId];
                delete next[resolvedPriceSurveyCompetitorId];
                return next;
            });
            refetchProofs();
            onProofsUpdated?.();
        } catch (error) {
            console.error('Error updating proofs:', error);
            showErrorNotification(
                error instanceof Error
                    ? error.message
                    : 'Failed to update proofs',
            );
        } finally {
            updateInFlightRef.current = false;
            setIsUploading(false);
            setUploadProgress(0);
        }
    }, [
        priceSurveyCompetitorId,
        competitorData?.competitor?.id,
        ensurePricesSavedForProofUpload,
        pendingProofs,
        proofsToDelete,
        deleteProof,
        generateUploadUrls,
        createProofs,
        refetchProofs,
        onProofsUpdated,
    ]);

    const changesCount = pendingProofs.length + proofsToDelete.length;

    const phoneNumber = stringifyNumber(competitorData?.competitor?.telNo);

    return (
        <>
            <Drawer
                opened={opened}
                onClose={handleCancel}
                position="right"
                size={500}
                withCloseButton={false}
                padding={0}
                classNames={{
                    content: 'bg-[#F4F5F7]',
                    body: 'bg-[#F4F5F7] h-full flex flex-col',
                }}
            >
                <div className="flex-1 flex flex-col min-h-0 bg-[#F4F5F7] w-full min-w-0 max-w-full">
                    <div className="px-6 py-6">
                        <Stack gap={16}>
                            <Stack gap={0} className="gap-1">
                                <Flex
                                    justify="space-between"
                                    align="flex-start"
                                    gap="md"
                                    wrap="nowrap"
                                    className="min-w-0"
                                >
                                    <Text
                                        fw={700}
                                        size="xl"
                                        className="text-[#003B7E] leading-tight min-w-0 flex-1 break-words"
                                    >
                                        {competitorData?.competitor?.name ||
                                            'Competitor'}
                                    </Text>
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        aria-label="Close competitor drawer"
                                        onClick={() => handleCancel()}
                                        className="hover:bg-gray-200 -mt-1 -mr-2 flex-shrink-0"
                                    >
                                        <IconX size={20} />
                                    </ActionIcon>
                                </Flex>

                                <When
                                    condition={
                                        !!competitorData?.competitor?.isMajor
                                    }
                                >
                                    <Badge
                                        color="yellow"
                                        variant="light"
                                        size="lg"
                                        radius="xl"
                                        className="normal-case font-medium w-fit"
                                        leftSection={
                                            <IconStarFilled
                                                size={16}
                                                aria-hidden
                                            />
                                        }
                                    >
                                        Major Competitor
                                    </Badge>
                                </When>
                            </Stack>

                            <Stack gap={12}>
                                {competitorData?.competitor?.address && (
                                    <Group
                                        gap={8}
                                        wrap="nowrap"
                                        align="flex-start"
                                    >
                                        <IconMapPin
                                            size={18}
                                            className="text-[#3b526b] flex-shrink-0 mt-0.5"
                                            stroke={1.5}
                                        />
                                        <Text
                                            size="sm"
                                            className="text-[#3b526b]"
                                        >
                                            {competitorData.competitor.address}
                                        </Text>
                                    </Group>
                                )}

                                <Flex gap={32} align="center">
                                    {phoneNumber && (
                                        <Group
                                            gap={8}
                                            wrap="nowrap"
                                            className="flex-shrink-0 group"
                                        >
                                            <IconPhone
                                                size={18}
                                                className="text-[#3b526b] group-hover:text-primary flex-shrink-0 transition-colors"
                                                stroke={1.5}
                                            />
                                            <Anchor
                                                href={`tel:${phoneNumber}`}
                                                size="sm"
                                                className="text-[#3b526b] group-hover:text-primary whitespace-nowrap hover:underline transition-colors"
                                            >
                                                {phoneNumber}
                                            </Anchor>
                                        </Group>
                                    )}

                                    {competitorData?.competitor?.email && (
                                        <Group
                                            gap={8}
                                            wrap="nowrap"
                                            className="group"
                                        >
                                            <IconMail
                                                size={18}
                                                className="text-[#3b526b] group-hover:text-primary flex-shrink-0 transition-colors"
                                                stroke={1.5}
                                            />
                                            <Anchor
                                                href={`mailto:${competitorData.competitor.email}`}
                                                size="sm"
                                                className="text-[#3b526b] group-hover:text-primary hover:underline transition-colors"
                                            >
                                                {
                                                    competitorData.competitor
                                                        .email
                                                }
                                            </Anchor>
                                        </Group>
                                    )}
                                </Flex>
                            </Stack>
                        </Stack>
                    </div>

                    <div className="px-6 pb-6 flex-1 overflow-y-auto">
                        <SurveyProofUpload
                            proofs={allProofs}
                            onAddProofs={handleAddProofs}
                            onRemoveProof={handleRemoveProof}
                            onViewProof={handleViewProof}
                            isUploading={isUploading || isSavingPrices}
                            disabled={readOnly}
                        />

                        <When condition={!readOnly}>
                            <Stack gap="sm" mt={24}>
                                <When condition={isUploading}>
                                    <Stack gap={6}>
                                        <Text size="sm" c="dimmed">
                                            Updating proofs…
                                        </Text>
                                        <Progress
                                            value={uploadProgress}
                                            size="sm"
                                            radius="xl"
                                            animated
                                            className="w-full"
                                            color="primary"
                                        />
                                    </Stack>
                                </When>
                                <Flex gap={12}>
                                    <Button
                                        radius="xl"
                                        size="sm"
                                        variant="filled"
                                        color="primary"
                                        className="flex-1"
                                        onClick={handleUpdate}
                                        loading={isUploading || isSavingPrices}
                                        disabled={
                                            !hasChanges ||
                                            isUploading ||
                                            isSavingPrices
                                        }
                                    >
                                        Update
                                        {changesCount > 0
                                            ? ` (${changesCount})`
                                            : ''}
                                    </Button>
                                </Flex>
                            </Stack>
                        </When>
                    </div>
                </div>
            </Drawer>

            <UnsavedChangesWarningModal
                opened={unsavedChangesModalOpen}
                onStay={() => setUnsavedChangesModalOpen(false)}
                onLeave={discardDraftAndClose}
            />
        </>
    );
}

export default SurveyCompetitorDrawer;
